/**
 * Recursively extract every File from a DataTransfer when the user drops
 * a folder. Uses webkitGetAsEntry() + FileSystemDirectoryReader, supported
 * on desktop in Chrome (2013+), Firefox 50+, Safari 11.1+, Edge.
 *
 * readEntries() returns up to 100 entries per call by spec, so we loop
 * until an empty batch arrives.
 */
export async function extractFilesFromDataTransfer(dt: DataTransfer): Promise<File[]> {
    const items = Array.from(dt.items);
    const out: File[] = [];

    for (const item of items) {
        if (item.kind !== 'file') continue;
        const entry = item.webkitGetAsEntry?.();
        if (entry) {
            await collectFiles(entry, out);
        } else {
            const file = item.getAsFile();
            if (file) out.push(file);
        }
    }

    return out;
}

async function collectFiles(entry: FileSystemEntry, out: File[]): Promise<void> {
    if (entry.isFile) {
        const file = await new Promise<File>((resolve, reject) => {
            (entry as FileSystemFileEntry).file(resolve, reject);
        });
        out.push(file);
        return;
    }

    if (entry.isDirectory) {
        const reader = (entry as FileSystemDirectoryEntry).createReader();
        const all = await readAllEntries(reader);
        for (const sub of all) {
            await collectFiles(sub, out);
        }
    }
}

async function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
    const all: FileSystemEntry[] = [];
    let batch: FileSystemEntry[];
    do {
        batch = await new Promise((resolve, reject) => {
            reader.readEntries(resolve, reject);
        });
        all.push(...batch);
    } while (batch.length > 0);
    return all;
}
